import React, { ReactElement } from 'react';
import styled, { ColorsTypes, FontsWeights, BreakpointsItems } from 'styled-components';
import { space, border, flexbox, layout, position, SpaceProps, BorderProps, FlexboxProps, LayoutProps, PositionProps, size } from 'styled-system';

type ButtonSizes = 'small' | 'medium' | 'large' | 'card';

type MyButtonAllProps = React.ButtonHTMLAttributes<HTMLButtonElement> & SpaceProps & BorderProps & FlexboxProps & LayoutProps & PositionProps;

interface MyButtonProps extends MyButtonAllProps {
  size?: ButtonSizes | Partial<Record<BreakpointsItems, ButtonSizes>>;
  fontWeight?: FontsWeights;

  color?: ColorsTypes;
  colorHover?: ColorsTypes;
  colorClick?: ColorsTypes;

  bg?: ColorsTypes | 'transparent';
  bgHover?: ColorsTypes | 'transparent';
  bgClick?: ColorsTypes | 'transparent';

  borderColor?: ColorsTypes;
  borderColorHover?: ColorsTypes;
  borderColorClick?: ColorsTypes;

  outline?: boolean;
  square?: boolean;
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
  buttonSize: ButtonSizes | Partial<Record<BreakpointsItems, ButtonSizes>>;
  fontWeight: FontsWeights;

  color: ColorsTypes;
  colorHover: ColorsTypes;
  colorClick: ColorsTypes;

  bg: ColorsTypes | 'transparent';
  bgHover: ColorsTypes | 'transparent';
  bgClick: ColorsTypes | 'transparent';

  borderColor: ColorsTypes | 'transparent';
  borderColorHover: ColorsTypes | 'transparent';
  borderColorClick: ColorsTypes | 'transparent';

  square?: boolean;
}

const Button = styled('button')<ButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid;
  border-color: ${({ theme, borderColor }) => (borderColor === 'transparent' ? 'transparent' : theme.colors[borderColor])};
  padding: 0;
  cursor: pointer;
  color: ${({ theme, color }) => theme.colors[color]};
  background: ${({ theme, bg }) => (bg === 'transparent' ? 'transparent' : theme.colors[bg])};
  transition: 0.2s;
  outline: transparent;
  padding: 0;

  text-transform: uppercase;
  transition: 0.2s;
  white-space: nowrap;

  ${({ buttonSize, square, theme: { fontSizes, mediaQueries } }) => {
    const sizes: Record<ButtonSizes, any> = {
      small: `
        height: 40px;
        border-radius: ${square ? 'var(--border-radius)' : '20px'};
        font-size: ${fontSizes[1]};
        padding-left: 40px;
        padding-right: 40px;
      `,

      medium: `
        height: 60px;
        border-radius: ${square ? 'var(--border-radius)' : '30px'};
        font-size: ${fontSizes[5]};
        padding-left: 60px;
        padding-right: 60px;
      `,

      large: `
        height: 80px;
        border-radius: ${square ? 'var(--border-radius)' : '40px'};
        font-size: ${fontSizes[5]};
        padding-left: 60px;
        padding-right: 60px;
      `,

      card: `
        height: 60px;
        border-radius: ${square ? 'var(--border-radius)' : '20px'};
        font-size: ${fontSizes[3]};
        padding-left: 40px;
        padding-right: 40px;
      `,
    };

    if (typeof buttonSize === 'object') {
      let buttonStyles = '';

      if (buttonSize.m) {
        buttonStyles = `
          ${buttonStyles}

          ${mediaQueries.mobile} {
            ${sizes[buttonSize.m]}
          }
        `;
      }

      if (buttonSize.t) {
        buttonStyles = `
          ${buttonStyles}

          ${mediaQueries.tablet} {
            ${sizes[buttonSize.t]}
          }
        `;
      }

      if (buttonSize.d) {
        buttonStyles = `
          ${buttonStyles}

          ${mediaQueries.desktop} {
            ${sizes[buttonSize.d]}
          }
        `;
      }

      return `
        ${buttonStyles}
      `;
    } else {
      switch (buttonSize) {
        case 'small':
          return sizes.small;

        case 'medium':
        default:
          return sizes.medium;

        case 'large':
          return sizes.large;
      }
    }
  }}

  ${space}
  ${border}
  ${flexbox}
  ${layout}
  ${position}
  ${size}

  color: ${({ theme, color }) => theme.colors[color]};
  font-weight: ${({ theme, fontWeight }) => theme.fontWeights[fontWeight]};

  &:hover {
    background: ${({ theme, bgHover }) => (bgHover === 'transparent' ? 'transparent' : theme.colors[bgHover])};

    color: ${({ theme, colorHover }) => theme.colors[colorHover]};

    border-color: ${({ theme, borderColorHover }) => (borderColorHover === 'transparent' ? 'transparent' : theme.colors[borderColorHover])};
  }

  &:active {
    background: ${({ theme, bgClick }) => (bgClick === 'transparent' ? 'transparent' : theme.colors[bgClick])};

    color: ${({ theme, colorClick }) => theme.colors[colorClick]};

    border-color: ${({ theme, borderColorClick }) => (borderColorClick === 'transparent' ? 'transparent' : theme.colors[borderColorClick])};
  }
`;

const MyButton = ({
  size,

  color,
  colorHover,
  colorClick,

  bg,
  bgHover,
  bgClick,

  borderColor,
  borderColorHover,
  borderColorClick,

  fontWeight,
  outline,
  children,
  square = true,

  ...buttonProps
}: MyButtonProps): ReactElement => {
  let myButtonProps = buttonProps;

  let buttonBg = bg || 'primary_400';
  let buttonBgHover = bgHover || (buttonBg === 'primary_400' ? 'primary_200' : buttonBg);
  let buttonBgClick = bgClick || (buttonBg === 'primary_400' ? 'primary_600' : buttonBg);

  let buttonColor = color || 'primary_contrast';
  let buttonColorHover = colorHover || (buttonColor === 'primary_contrast' ? 'primary_contrast' : buttonColor);
  let buttonColorClick = colorClick || (buttonColor === 'primary_contrast' ? 'primary_contrast' : buttonColor);

  let buttonBorderColor = borderColor || buttonBg;
  let buttonBorderColorHover = borderColorHover || (borderColor ? borderColor : buttonBgHover);
  let buttonBorderColorClick = borderColorClick || (borderColor ? borderColor : buttonBgClick);

  let buttonSize = size || 'small';

  let buttonFontWeight = fontWeight || 'bold';

  if (outline) {
    if (buttonBg === 'primary_400') {
      buttonBg = 'transparent';
      buttonBorderColor = 'primary_400';
      buttonColor = 'primary_400';

      buttonBorderColorHover = 'primary_200';
      buttonColorHover = 'primary_contrast';

      buttonBorderColorClick = 'primary_600';
    }

    if (buttonBg === 'gray_500') {
      buttonBg = 'transparent';
      buttonBorderColor = 'gray_500';
      buttonColor = 'gray_500';

      buttonBorderColorHover = 'gray_500';
      buttonColorHover = 'primary_contrast';

      buttonBorderColorClick = 'primary_600';
    }

    // if (buttonBg === 'primary_400') {
    //   myButtonProps.borderWidth = 1;
    //   myButtonProps.borderStyle = 'solid';
    //   myButtonProps.borderColor = buttonBg;
    //   buttonBg = 'transparent';
    // }
  }

  return (
    <Button
      bg={buttonBg}
      bgHover={buttonBgHover}
      bgClick={buttonBgClick}
      color={buttonColor}
      colorHover={buttonColorHover}
      colorClick={buttonColorClick}
      borderColor={buttonBorderColor}
      borderColorHover={buttonBorderColorHover}
      borderColorClick={buttonBorderColorClick}
      buttonSize={buttonSize}
      fontWeight={buttonFontWeight}
      square={square}
      {...myButtonProps}
    >
      {children}
    </Button>
  );
};
export default MyButton;
